import { z } from 'zod';
import {
  ChangeTypeSchema,
  ConfidenceSchema,
  ResponseTypeSchema,
  StandardStatusSchema,
} from './enums.js';

export const StandardSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string(),
  issuingBody: z.string(),
  status: StandardStatusSchema,
  /**
   * Offer a Statement of Applicability for this standard. The SOA is an
   * ISO/IEC 27001 artefact (clause 6.1.3 d), so it is off unless a system
   * admin switches it on for a standard with a control set.
   */
  supportsSoa: z.boolean(),
  currentVersionId: z.string().nullable(),
  supersededById: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Standard = z.infer<typeof StandardSchema>;

export const CreateStandardInputSchema = z.object({
  code: z
    .string()
    .min(2)
    .regex(/^[a-z0-9][a-z0-9-]*$/),
  name: z.string().min(1),
  description: z.string().default(''),
  issuingBody: z.string().default(''),
  supportsSoa: z.boolean().default(false),
});
export type CreateStandardInput = z.infer<typeof CreateStandardInputSchema>;

export const UpdateStandardInputSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().optional(),
  issuingBody: z.string().optional(),
  status: StandardStatusSchema.optional(),
  supersededById: z.string().nullable().optional(),
  supportsSoa: z.boolean().optional(),
});
export type UpdateStandardInput = z.infer<typeof UpdateStandardInputSchema>;

export const StandardVersionSchema = z.object({
  id: z.string(),
  standardId: z.string(),
  label: z.string(),
  status: StandardStatusSchema,
  publicationDate: z.string().nullable(),
  effectiveDate: z.string().nullable(),
  retirementDate: z.string().nullable(),
  changeSummary: z.string(),
  migrationGuidance: z.string(),
  createdAt: z.string(),
});
export type StandardVersion = z.infer<typeof StandardVersionSchema>;

export const QuestionSchema = z.object({
  id: z.string(),
  clauseId: z.string(),
  sortOrder: z.number().int(),
  text: z.string(),
  helpText: z.string(),
  responseType: ResponseTypeSchema,
  /** JSON for multiple-choice options / table column definitions. */
  options: z.unknown().nullable(),
  requiredEvidence: z.boolean(),
  evidenceGuidance: z.string(),
  aiPromptTemplate: z.string(),
  validationRules: z.unknown().nullable(),
  completionCriteria: z.string(),
});
export type Question = z.infer<typeof QuestionSchema>;

export const ClauseSchema = z.object({
  id: z.string(),
  versionId: z.string(),
  parentClauseId: z.string().nullable(),
  sortOrder: z.number().int(),
  clauseNumber: z.string(),
  title: z.string(),
  description: z.string(),
  mandatory: z.boolean(),
  evidenceRequirements: z.string(),
  guidanceNotes: z.string(),
  /** Suggested shared-corpus item keys supporting this clause. */
  corpusKeys: z.array(z.string()),
});
export type Clause = z.infer<typeof ClauseSchema>;

export interface ClauseTreeNode extends Clause {
  questions: Question[];
  children: ClauseTreeNode[];
}
export const ClauseTreeNodeSchema: z.ZodType<ClauseTreeNode> = z.lazy(() =>
  ClauseSchema.extend({
    questions: z.array(QuestionSchema),
    children: z.array(ClauseTreeNodeSchema),
  }),
) as z.ZodType<ClauseTreeNode>;

export const StandardVersionDetailSchema = z.object({
  standard: StandardSchema,
  version: StandardVersionSchema,
  clauses: z.array(ClauseTreeNodeSchema),
});
export type StandardVersionDetail = z.infer<typeof StandardVersionDetailSchema>;

// ---------- Import bundle (seed data + import/export endpoints) ----------

export const ImportQuestionSchema = z.object({
  text: z.string().min(1),
  helpText: z.string().default(''),
  responseType: ResponseTypeSchema.default('markdown'),
  options: z.unknown().optional(),
  requiredEvidence: z.boolean().default(false),
  evidenceGuidance: z.string().default(''),
  aiPromptTemplate: z.string().default(''),
  validationRules: z.unknown().optional(),
  completionCriteria: z.string().default(''),
});
export type ImportQuestion = z.infer<typeof ImportQuestionSchema>;

export const ImportClauseSchema = z.object({
  /** Clause number unique within the version, e.g. "4.1" or "A.5.1". */
  number: z.string().min(1),
  /** Parent clause number; omit for top level. */
  parentNumber: z.string().optional(),
  title: z.string().min(1),
  description: z.string().default(''),
  mandatory: z.boolean().default(true),
  evidenceRequirements: z.string().default(''),
  guidanceNotes: z.string().default(''),
  corpusKeys: z.array(z.string()).default([]),
  questions: z.array(ImportQuestionSchema).default([]),
});
export type ImportClause = z.infer<typeof ImportClauseSchema>;

export const StandardImportBundleSchema = z.object({
  standard: z.object({
    code: z
      .string()
      .min(2)
      .regex(/^[a-z0-9][a-z0-9-]*$/),
    name: z.string().min(1),
    description: z.string().default(''),
    issuingBody: z.string().default(''),
    /**
     * Optional on purpose: a bundle that omits it leaves the operator's own
     * choice alone on re-import (only a bundle that states it overwrites).
     */
    supportsSoa: z.boolean().optional(),
  }),
  version: z.object({
    label: z.string().min(1),
    publicationDate: z.string().optional(),
    effectiveDate: z.string().optional(),
    changeSummary: z.string().default(''),
    migrationGuidance: z.string().default(''),
  }),
  clauses: z.array(ImportClauseSchema).min(1),
});
export type StandardImportBundle = z.infer<typeof StandardImportBundleSchema>;

// ---------- Version diff / migration ----------

export const ClauseDiffEntrySchema = z.object({
  changeType: ChangeTypeSchema,
  clauseNumber: z.string(),
  fromClauseId: z.string().nullable(),
  toClauseId: z.string().nullable(),
  title: z.string(),
  questionCountFrom: z.number().int(),
  questionCountTo: z.number().int(),
});
export type ClauseDiffEntry = z.infer<typeof ClauseDiffEntrySchema>;

export const VersionDiffSchema = z.object({
  fromVersionId: z.string(),
  toVersionId: z.string(),
  entries: z.array(ClauseDiffEntrySchema),
  summary: z.object({
    new: z.number().int(),
    changed: z.number().int(),
    removed: z.number().int(),
    unchanged: z.number().int(),
  }),
});
export type VersionDiff = z.infer<typeof VersionDiffSchema>;

export const ClauseMappingInputSchema = z.object({
  fromVersionId: z.string(),
  fromClauseId: z.string().nullable(),
  toClauseId: z.string().nullable(),
  changeType: ChangeTypeSchema,
  notes: z.string().default(''),
});
export type ClauseMappingInput = z.infer<typeof ClauseMappingInputSchema>;

// ---------- Crosswalk (corpus item → standard clause) ----------

export const CrosswalkMappingSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  corpusItemId: z.string(),
  standardId: z.string(),
  versionId: z.string().nullable(),
  clauseId: z.string().nullable(),
  confidence: ConfidenceSchema,
  notes: z.string(),
  requiresReview: z.boolean(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
});
export type CrosswalkMapping = z.infer<typeof CrosswalkMappingSchema>;

export const CreateCrosswalkInputSchema = z.object({
  corpusItemId: z.string(),
  standardId: z.string(),
  versionId: z.string().nullable().optional(),
  clauseId: z.string().nullable().optional(),
  confidence: ConfidenceSchema.default('medium'),
  notes: z.string().default(''),
  requiresReview: z.boolean().default(false),
});
export type CreateCrosswalkInput = z.infer<typeof CreateCrosswalkInputSchema>;
